import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";
import { HeroSection } from "../components/home/HeroSection";
import { AboutSection } from "../components/home/AboutSection";
import { PillarsSection } from "../components/home/PillarsSection";
import { CtaBridge } from "../components/home/CtaBridge";
import { JourneySection } from "../components/home/JourneySection";
import { ContactSection } from "../components/home/ContactSection";
import "../home.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pageRef = useRef(null);

  /* Seed blog on mount */
  useEffect(() => { axios.post(`${API}/blog/seed`).catch(() => {}); }, []);

  /* Scroll detection for nav */
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  /* IntersectionObserver — triggers once per element at threshold 0.12 */
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal, .scale-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });

  /* Lock body on mobile menu */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const scrollTo = useCallback((id) => {
    setMobileOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 50);
  }, []);

  return (
    <div ref={pageRef} className="hm-body" style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink)" }}>
      <SEO
        title="Tulsi Giri — Social Entrepreneur, Food Systems, Nepal & Switzerland"
        description="Social entrepreneur for two decades building food systems connecting farmers to markets, Nepal to the World. Co-founded Development Voyage, KHETI, Sarvaguna, Food Networks."
        path="/"
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Person",
          "name": "Tulsi Giri", "url": "https://www.tulsigiri.com",
          "jobTitle": "Social Entrepreneur",
          "description": "Social entrepreneur for two decades building food systems connecting farmers to markets, Nepal to the World.",
          "knowsAbout": ["Food Systems", "Social Entrepreneurship", "Sustainable Development", "Agricultural Technology", "Cross-Cultural Exchange"],
          "address": { "@type": "PostalAddress", "addressLocality": "Pokhara", "addressCountry": "Nepal" },
          "sameAs": ["https://www.linkedin.com/in/tulsi-giri-91448517"],
          "email": "giritulsi@gmail.com",
          "worksFor": [
            { "@type": "Organization", "name": "Development Voyage", "url": "https://developmentvoyage.com" },
            { "@type": "Organization", "name": "KHETI", "url": "https://www.kheti.farm" },
            { "@type": "Organization", "name": "Sarvaguna Kitchen", "url": "https://www.sarvagunakitchen.com" },
            { "@type": "Organization", "name": "Food Networks", "url": "https://www.foodnetworks.ch" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "WebSite",
          "name": "Tulsi Giri", "url": "https://www.tulsigiri.com",
          "description": "Personal website of Tulsi Giri — social entrepreneur building food systems across Nepal and Switzerland."
        })}</script>
      </SEO>

      {/* NAV */}
      <header className={`hm-nav ${scrolled ? "scrolled" : ""}`} data-testid="home-nav">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
          <button onClick={() => scrollTo("top")} className="hm-display text-xl tracking-tight" style={{ color: "var(--ink)", background: "none", border: "none", cursor: "pointer" }} data-testid="nav-wordmark">
            Tulsi <span style={{ color: "var(--green)" }}>Giri</span>
          </button>
          <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
            {[["About", "about"], ["Work", "work"], ["Journey", "journey"]].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hm-body text-sm font-medium hover:text-[var(--ink)] transition-colors" style={{ color: "var(--ink3)", background: "none", border: "none", cursor: "pointer" }} data-testid={`nav-${id}`}>{label}</button>
            ))}
            <Link to="/blog" className="hm-body text-sm font-medium hover:text-[var(--ink)] transition-colors" style={{ color: "var(--ink3)", textDecoration: "none" }} data-testid="nav-updates">Updates</Link>
          </nav>
          <button onClick={() => scrollTo("contact")} className="hm-btn-filled hidden md:inline-flex" style={{ padding: "8px 22px", fontSize: "0.85rem" }} data-testid="nav-cta">Let's Connect</button>
          <button className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }} data-testid="nav-hamburger"><Menu size={24} /></button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div className={`hm-overlay ${mobileOpen ? "open" : ""}`} data-testid="mobile-menu">
        <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }} data-testid="mobile-close"><X size={28} /></button>
        {[["About", "about"], ["Work", "work"], ["Journey", "journey"]].map(([label, id]) => (
          <button key={id} onClick={() => scrollTo(id)} className="hm-display text-2xl" style={{ color: "var(--ink)", background: "none", border: "none", cursor: "pointer" }}>{label}</button>
        ))}
        <Link to="/blog" onClick={() => setMobileOpen(false)} className="hm-display text-2xl" style={{ color: "var(--ink)", textDecoration: "none" }}>Updates</Link>
        <button onClick={() => scrollTo("contact")} className="hm-btn-filled mt-4">Let's Connect</button>
      </div>

      {/* SECTIONS */}
      <HeroSection scrollTo={scrollTo} />
      <AboutSection />
      <PillarsSection />
      <CtaBridge scrollTo={scrollTo} />
      <JourneySection />
      <ContactSection />
    </div>
  );
}
